/**
 * Assessment History Page
 *
 * Created from HTML: assessment_history_screen_(variant_1/2)_-_unified_layout/code.html
 * Updated to use UnifiedHeader and UnifiedFooter with consistent layout
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { apiService } from '@vsat/shared/lib/api';
import { toast } from 'sonner';
import { UnifiedHeader } from '../components/UnifiedHeader';
import { UnifiedFooter } from '../components/UnifiedFooter';
import { SEOHead } from '../components/SEOHead';

interface AssessmentRecord {
  id: string;
  date: string;
  time: string;
  level: string;
  topGroup: string | null;
  topGroupColor: string | null;
  status: 'completed' | 'in-progress' | 'expired';
  hasFullReportAccess: boolean;
}

interface ExamAttempt {
  id: string;
  examId: string;
  examTitle: string;
  date: string;
  time: string;
  score: number;
  maxScore: number;
  correctCount: number;
  incorrectCount: number;
  unansweredCount: number;
  totalQuestions: number;
  duration: number; // in minutes
  timeSpent: number; // in seconds
  passed: boolean;
  resultId?: string; // for linking to result detail
}

interface IncompleteSession {
  sessionId: string;
  examId: string;
  examTitle: string;
  subject: string;
  examType: string;
  systemContext: string;
  startedAt: string;
  remainingTime: number;
}

export function AssessmentHistoryPage() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'cat' | 'thpt'>('cat');
  const [assessments, setAssessments] = useState<AssessmentRecord[]>([]);
  const [examAttempts, setExamAttempts] = useState<ExamAttempt[]>([]);
  const [incompleteSessions, setIncompleteSessions] = useState<IncompleteSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [examLoading, setExamLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load assessments from backend
  useEffect(() => {
    const loadAssessments = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user's assessment history from backend
        const response = await apiService.getUserCareerOrientationAssessments(50, 0);
        const assessmentList = response.data || [];

        const transformedAssessments: AssessmentRecord[] = assessmentList.map((assessment: any) => {
          const dateValue = new Date(assessment.date);
          const dateStr = dateValue.toLocaleDateString('vi-VN');
          const timeStr = dateValue.toLocaleTimeString('vi-VN', {
            hour: '2-digit',
            minute: '2-digit',
          });

          // Use topGroup directly from API response
          const topGroup = assessment.topGroup;

          const getGroupColor = (groupName: string): string => {
            const colorMap: Record<string, string> = {
              'KỸ THUẬT': 'bg-red-500',
              'NGHIÊN CỨU': 'bg-blue-500',
              'NGHỆ THUẬT': 'bg-orange-500',
              'XÃ HỘI': 'bg-green-500',
              'QUẢN LÝ': 'bg-purple-500',
              'NGHIỆP VỤ': 'bg-gray-500',
            };
            return colorMap[groupName?.toUpperCase()] || 'bg-gray-500';
          };

          return {
            id: assessment.id,
            date: dateStr,
            time: timeStr,
            level: assessment.educationLevel || 'THPT',
            topGroup: topGroup ? topGroup.name : null,
            topGroupColor: topGroup ? getGroupColor(topGroup.name) : null,
            status:
              assessment.status === 'completed'
                ? 'completed'
                : assessment.status === 'expired'
                  ? 'expired'
                  : 'in-progress',
            hasFullReportAccess: assessment.hasFullReportAccess || false,
          };
        });

        setAssessments(transformedAssessments);
      } catch (err: any) {
        console.error('Failed to load assessment history:', err);
        setError('Không thể tải lịch sử đánh giá. Vui lòng thử lại sau.');
        toast.error('Không thể tải lịch sử đánh giá');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadAssessments();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Load exam attempts when switching to THPT tab
  useEffect(() => {
    const loadExamHistory = async () => {
      if (activeTab !== 'thpt' || !user) return;

      try {
        setExamLoading(true);

        // Fetch exam results from backend API
        const { data: results } = await apiService.getUserExamResults(50, 0);

        // Transform API response to ExamAttempt format
        const attempts: ExamAttempt[] = results.map((result: any) => ({
          id: result.id,
          examId: result.examId,
          examTitle: result.examTitle,
          date: result.date,
          time: result.time,
          score: result.score,
          maxScore: result.maxScore,
          correctCount: result.correctCount,
          incorrectCount: result.incorrectCount,
          unansweredCount: result.unansweredCount,
          totalQuestions: result.totalQuestions,
          duration: result.duration,
          timeSpent: result.timeSpent,
          passed: result.passed,
          resultId: result.id, // result.id IS the resultId from backend
        }));

        setExamAttempts(attempts);
      } catch (err: any) {
        console.error('Failed to load exam history:', err);
        toast.error('Không thể tải lịch sử thi');
      } finally {
        setExamLoading(false);
      }
    };

    loadExamHistory();
  }, [activeTab, user]);

  // Load incomplete sessions
  useEffect(() => {
    const loadIncompleteSessions = async () => {
      if (activeTab !== 'thpt' || !user) return;
      try {
        const sessions = await apiService.getIncompleteSessions();
        setIncompleteSessions(sessions);
      } catch (err) {
        console.error('Failed to load incomplete sessions', err);
      }
    };
    loadIncompleteSessions();
  }, [activeTab, user]);

  return (
    <div className='bg-background-light dark:bg-background-dark text-gray-800 dark:text-gray-100 font-sans min-h-screen flex flex-col transition-colors duration-200'>
      <SEOHead
        title='Lịch sử làm bài | Hướng nghiệp CAT'
        description='Xem lại lịch sử làm bài thi thử và trắc nghiệm hướng nghiệp CAT.'
        noindex
      />
      <UnifiedHeader />
      <main className='flex-1 pt-20 pb-12 lg:pt-24'>
        {/* Main Content (lines 110-272) */}
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          <div className='flex flex-col lg:flex-row gap-8'>
            {/* Main Section (lines 112-242) */}
            <div className='flex-1 space-y-6'>
              {/* Header (lines 113-118) */}
              <div className='flex justify-between items-end'>
                <div>
                  <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>Lịch sử</h1>
                  <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                    {activeTab === 'cat'
                      ? 'Theo dõi quá trình và kết quả bài Hướng nghiệp CAT của bạn'
                      : 'Xem lịch sử làm bài thi thử THPT của bạn'}
                  </p>
                </div>
              </div>

              {/* Tabs */}
              <div className='flex gap-2 border-b border-gray-200 dark:border-gray-700'>
                <button
                  onClick={() => setActiveTab('cat')}
                  className={`px-4 py-2 font-medium text-sm transition-all ${
                    activeTab === 'cat'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <span className='flex items-center gap-2'>
                    <span className='material-icons-round text-base'>psychology</span>
                    Hướng nghiệp CAT
                  </span>
                </button>
                <button
                  onClick={() => setActiveTab('thpt')}
                  className={`px-4 py-2 font-medium text-sm transition-all ${
                    activeTab === 'thpt'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <span className='flex items-center gap-2'>
                    <span className='material-icons-round text-base'>school</span>
                    Luyện thi THPT
                  </span>
                </button>
              </div>

              {/* CAT History Content */}
              {activeTab === 'cat' && (
                <div className='space-y-6'>
                  {/* Status Card - Only show when there are completed assessments */}
                  {assessments.some(a => a.status === 'completed') &&
                    (() => {
                      const latestCompleted = assessments.find(a => a.status === 'completed');
                      return (
                        <div className='bg-surface-light dark:bg-surface-dark rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 relative overflow-hidden'>
                          <div className='absolute top-0 right-0 p-4 opacity-10 pointer-events-none'>
                            <span className='material-icons-round text-9xl text-primary transform rotate-12'>
                              assignment_turned_in
                            </span>
                          </div>
                          <div className='relative z-10'>
                            <div className='flex justify-between items-start mb-4'>
                              <div>
                                <h2 className='text-xl font-bold text-gray-900 dark:text-white mb-1'>
                                  Trạng thái bài Hướng nghiệp CAT
                                </h2>
                                <p className='text-sm text-gray-500 dark:text-gray-400'>
                                  Đã hoàn thành ngày{' '}
                                  <span className='font-medium text-gray-800 dark:text-gray-200'>
                                    {latestCompleted?.date || 'N/A'}
                                  </span>
                                </p>
                              </div>
                              <span className='px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold rounded-full border border-green-200 dark:border-green-800'>
                                Hoàn thành
                              </span>
                            </div>
                            <div className='bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-4 border border-gray-100 dark:border-gray-700'>
                              <p className='text-gray-700 dark:text-gray-300 leading-relaxed'>
                                <span className='font-semibold text-gray-900 dark:text-white'>
                                  Nhóm nổi bật:
                                </span>{' '}
                                {latestCompleted?.topGroup ? (
                                  <span
                                    className={`px-2 py-0.5 ${latestCompleted.topGroupColor} text-white text-sm font-medium rounded`}
                                  >
                                    {latestCompleted.topGroup}
                                  </span>
                                ) : (
                                  <span className='text-gray-500'>Chưa xác định</span>
                                )}
                              </p>
                            </div>
                            <Link
                              to={`/huong-nghiep-cat?screen=${latestCompleted?.hasFullReportAccess ? 'full-report' : 'free-result'}&sessionId=${latestCompleted?.id}`}
                              className='inline-flex items-center text-sm text-primary hover:text-primary/80 font-medium transition-colors'
                            >
                              <span className='material-icons-round text-base mr-1'>
                                visibility
                              </span>
                              Xem báo cáo chi tiết
                            </Link>
                          </div>
                        </div>
                      );
                    })()}

                  {/* History Table (lines 147-241) */}
                  <div className='bg-surface-light dark:bg-surface-dark rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden'>
                    <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center'>
                      <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                        Danh sách bài làm
                      </h3>
                      <div className='flex gap-2'>
                        <button className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'>
                          <span className='material-icons-round'>filter_list</span>
                        </button>
                        <button className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'>
                          <span className='material-icons-round'>sort</span>
                        </button>
                      </div>
                    </div>
                    <div className='overflow-x-auto'>
                      {loading ? (
                        <div className='flex items-center justify-center py-16'>
                          <div className='text-center'>
                            <div className='inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4'></div>
                            <p className='text-gray-500 dark:text-gray-400'>
                              Đang tải lịch sử đánh giá...
                            </p>
                          </div>
                        </div>
                      ) : assessments.length === 0 ? (
                        <div className='flex flex-col items-center justify-center py-16 px-4'>
                          <div className='text-gray-400 dark:text-gray-500 mb-4'>
                            <span className='material-icons-round' style={{ fontSize: '64px' }}>
                              assignment
                            </span>
                          </div>
                          <h3 className='text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2'>
                            Chưa có bài đánh giá nào
                          </h3>
                          <p className='text-sm text-gray-500 dark:text-gray-400 text-center max-w-md mb-6'>
                            Bạn chưa hoàn thành bài đánh giá CAT nào. Hãy bắt đầu làm bài để khám
                            phá định hướng nghề nghiệp phù hợp với bạn!
                          </p>
                          <Link
                            to='/huong-nghiep-cat'
                            className='inline-flex items-center px-6 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-colors'
                          >
                            <span className='material-icons-round mr-2'>play_arrow</span>
                            Bắt đầu đánh giá
                          </Link>
                        </div>
                      ) : (
                        <table className='w-full text-left border-collapse'>
                          <thead>
                            <tr className='bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold tracking-wider'>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap'>
                                Ngày/Giờ
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap'>
                                Định hướng
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap'>
                                Top 1
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap'>
                                Trạng thái
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap'>
                                Báo cáo
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap'>
                                Thao tác
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {assessments.map(assessment => (
                              <tr
                                key={assessment.id}
                                className='hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b border-gray-200 dark:border-gray-700 last:border-b-0'
                              >
                                <td className='px-4 py-3 whitespace-nowrap'>
                                  <div className='text-sm font-medium text-gray-900 dark:text-white'>
                                    {assessment.date}
                                  </div>
                                  <div className='text-xs text-gray-500 dark:text-gray-400'>
                                    {assessment.time}
                                  </div>
                                </td>
                                <td className='px-4 py-3 whitespace-nowrap'>
                                  <span
                                    className={`px-2 py-0.5 ${assessment.level === 'THCS' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800'} text-xs font-medium rounded border`}
                                  >
                                    {assessment.level === 'THCS' ? 'Học nghề' : 'Ngành ĐH'}
                                  </span>
                                </td>
                                <td className='px-4 py-3 whitespace-nowrap'>
                                  {assessment.topGroup ? (
                                    <span
                                      className={`px-2 py-0.5 ${assessment.topGroupColor} text-white text-xs font-medium rounded`}
                                    >
                                      {assessment.topGroup}
                                    </span>
                                  ) : (
                                    <span className='text-gray-400 dark:text-gray-500 text-xs'>
                                      Chưa có
                                    </span>
                                  )}
                                </td>
                                <td className='px-4 py-3 whitespace-nowrap'>
                                  {assessment.status === 'completed' ? (
                                    <span className='px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded border border-green-200 dark:border-green-800'>
                                      Xong
                                    </span>
                                  ) : assessment.status === 'expired' ? (
                                    <span className='px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded border border-gray-300 dark:border-gray-600'>
                                      Hết hạn
                                    </span>
                                  ) : (
                                    <span className='px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-medium rounded border border-yellow-200 dark:border-yellow-800'>
                                      Đang làm
                                    </span>
                                  )}
                                </td>
                                <td className='px-4 py-3 whitespace-nowrap'>
                                  {assessment.hasFullReportAccess ? (
                                    <span className='inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium rounded border border-amber-200 dark:border-amber-800'>
                                      <span className='material-icons-round text-xs'>verified</span>
                                      Đã mở khóa
                                    </span>
                                  ) : (
                                    <span className='px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs font-medium rounded border border-gray-200 dark:border-gray-600'>
                                      Free
                                    </span>
                                  )}
                                </td>
                                <td className='px-4 py-3 whitespace-nowrap'>
                                  <div className='flex gap-2'>
                                    {assessment.status === 'completed' ? (
                                      <Link
                                        to={`/huong-nghiep-cat?screen=${assessment.hasFullReportAccess ? 'full-report' : 'free-result'}&sessionId=${assessment.id}`}
                                        className='inline-flex items-center text-sm text-primary hover:text-primary/80 font-medium transition-colors'
                                      >
                                        <span className='material-icons-round text-base mr-1'>
                                          visibility
                                        </span>
                                        Xem
                                      </Link>
                                    ) : assessment.status === 'expired' ? (
                                      <Link
                                        to='/huong-nghiep-cat?screen=intro'
                                        className='inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 font-medium transition-colors'
                                      >
                                        <span className='material-icons-round text-base mr-1'>
                                          refresh
                                        </span>
                                        Làm lại
                                      </Link>
                                    ) : (
                                      <Link
                                        to={`/huong-nghiep-cat?screen=questions&sessionId=${assessment.id}`}
                                        className='inline-flex items-center text-sm text-primary hover:text-primary/80 font-medium transition-colors'
                                      >
                                        <span className='material-icons-round text-base mr-1'>
                                          play_arrow
                                        </span>
                                        Tiếp tục
                                      </Link>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                    {/* Pagination - Only show when has assessments */}
                    {assessments.length > 0 && (
                      <div className='px-6 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'>
                        <div className='text-xs text-gray-500 dark:text-gray-400'>
                          Hiển thị {assessments.length} kết quả
                        </div>
                        <div className='flex gap-1'>
                          <button
                            disabled
                            className='px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 text-xs hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed'
                          >
                            Trước
                          </button>
                          <button
                            disabled
                            className='px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 text-xs hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed'
                          >
                            Sau
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* THPT Exam History Content */}
              {activeTab === 'thpt' && (
                <div className='space-y-6'>
                  {incompleteSessions.length > 0 && (
                    <div className='space-y-4'>
                      <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2'>
                        <span className='material-icons-round text-yellow-600 dark:text-yellow-400'>
                          pending_actions
                        </span>
                        Bài thi đang làm dở
                      </h3>
                      <div className='grid gap-4 md:grid-cols-2'>
                        {incompleteSessions.map(session => (
                          <div
                            key={session.sessionId}
                            className='bg-surface-light dark:bg-surface-dark border border-yellow-200 dark:border-yellow-900/50 rounded-xl p-5 shadow-sm relative overflow-hidden group hover:shadow-md transition-shadow'
                          >
                            <div className='absolute top-0 right-0 p-4 opacity-5 pointer-events-none'>
                              <span className='material-icons-round text-8xl text-yellow-500 transform rotate-12'>
                                timer
                              </span>
                            </div>

                            <div className='relative z-10'>
                              <div className='flex justify-between items-start mb-3'>
                                <span className='px-2.5 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-semibold rounded-full border border-yellow-200 dark:border-yellow-800'>
                                  Đang làm
                                </span>
                                <span className='text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1'>
                                  <span className='material-icons-round text-xs'>schedule</span>
                                  {new Date(session.startedAt).toLocaleDateString('vi-VN')}
                                </span>
                              </div>

                              <h4 className='font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 min-h-[3rem]'>
                                {session.examTitle}
                              </h4>

                              <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4'>
                                <span className='flex items-center gap-1'>
                                  <span className='material-icons-round text-base'>subject</span>
                                  {session.subject}
                                </span>
                                <span className='text-gray-300 dark:text-gray-600'>|</span>
                                <span className='flex items-center gap-1'>
                                  <span className='material-icons-round text-base'>
                                    hourglass_empty
                                  </span>
                                  Còn {Math.ceil(session.remainingTime / 60)} phút
                                </span>
                              </div>

                              <Link
                                to={`/thi-thu-thpt/${session.examId}`}
                                className='w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white py-2.5 rounded-lg font-medium transition-colors text-sm'
                              >
                                <span className='material-icons-round'>play_circle</span>
                                Tiếp tục làm bài
                              </Link>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className='bg-surface-light dark:bg-surface-dark rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden'>
                    <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'>
                      <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                        Lịch sử làm bài thi
                      </h3>
                    </div>
                    <div className='overflow-x-auto'>
                      {examLoading ? (
                        <div className='flex items-center justify-center py-16'>
                          <div className='text-center'>
                            <div className='inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4'></div>
                            <p className='text-gray-500 dark:text-gray-400'>
                              Đang tải lịch sử thi...
                            </p>
                          </div>
                        </div>
                      ) : examAttempts.length === 0 ? (
                        <div className='flex flex-col items-center justify-center py-16 px-4'>
                          <div className='text-gray-400 dark:text-gray-500 mb-4'>
                            <span className='material-icons-round' style={{ fontSize: '64px' }}>
                              quiz
                            </span>
                          </div>
                          <h3 className='text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2'>
                            Chưa có bài thi nào
                          </h3>
                          <p className='text-sm text-gray-500 dark:text-gray-400 text-center max-w-md mb-6'>
                            Bạn chưa làm đề thi thử THPT nào. Hãy bắt đầu luyện tập để chuẩn bị tốt
                            cho kỳ thi!
                          </p>
                          <Link
                            to='/thi-thu-thpt'
                            className='inline-flex items-center px-6 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-colors'
                          >
                            <span className='material-icons-round mr-2'>play_arrow</span>
                            Bắt đầu luyện thi
                          </Link>
                        </div>
                      ) : (
                        <table className='w-full text-left border-collapse'>
                          <thead>
                            <tr className='bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold tracking-wider'>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700'>
                                Đề thi
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap'>
                                Ngày/Giờ
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-center'>
                                Điểm
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-center'>
                                Kết quả
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700 text-center whitespace-nowrap'>
                                Thời gian
                              </th>
                              <th className='px-4 py-3 border-b border-gray-200 dark:border-gray-700'>
                                Thao tác
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {examAttempts.map(attempt => (
                              <tr
                                key={attempt.id}
                                className='hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b border-gray-200 dark:border-gray-700 last:border-b-0'
                              >
                                <td className='px-4 py-3'>
                                  <div className='text-sm font-medium text-gray-900 dark:text-white'>
                                    {attempt.examTitle}
                                  </div>
                                </td>
                                <td className='px-4 py-3 whitespace-nowrap'>
                                  <div className='text-sm text-gray-900 dark:text-white'>
                                    {attempt.date}
                                  </div>
                                  <div className='text-xs text-gray-500 dark:text-gray-400'>
                                    {attempt.time}
                                  </div>
                                </td>
                                <td className='px-4 py-3 text-center'>
                                  <div className='text-lg font-bold text-primary'>
                                    {attempt.score.toFixed(2)}
                                  </div>
                                  <div className='text-xs text-gray-500 dark:text-gray-400'>
                                    / {attempt.maxScore}
                                  </div>
                                </td>
                                <td className='px-4 py-3 text-center'>
                                  <div className='text-xs space-y-1'>
                                    <div className='text-green-600 dark:text-green-400'>
                                      ✓ {attempt.correctCount} đúng
                                    </div>
                                    <div className='text-red-600 dark:text-red-400'>
                                      ✗ {attempt.incorrectCount} sai
                                    </div>
                                    {attempt.unansweredCount > 0 && (
                                      <div className='text-gray-500 dark:text-gray-400'>
                                        - {attempt.unansweredCount} bỏ qua
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className='px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400'>
                                  {Math.floor(attempt.timeSpent / 60)}:
                                  {(attempt.timeSpent % 60).toString().padStart(2, '0')}
                                  <div className='text-xs text-gray-500'>
                                    / {attempt.duration} phút
                                  </div>
                                </td>
                                <td className='px-4 py-3'>
                                  <div className='flex flex-col gap-1'>
                                    {attempt.resultId && (
                                      <Link
                                        to={`/ket-qua/${attempt.resultId}`}
                                        className='inline-flex items-center text-sm text-primary hover:text-primary/80 font-medium transition-colors'
                                      >
                                        <span className='material-icons-round text-base mr-1'>
                                          visibility
                                        </span>
                                        Xem kết quả
                                      </Link>
                                    )}
                                    <Link
                                      to={`/thi-thu-thpt/${attempt.examId}`}
                                      className='inline-flex items-center text-sm text-gray-500 hover:text-primary font-medium transition-colors'
                                    >
                                      <span className='material-icons-round text-base mr-1'>
                                        refresh
                                      </span>
                                      Làm lại
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar (lines 243-270) */}
            <div className='lg:w-80 flex-shrink-0 space-y-6 mt-6 lg:mt-12'>
              {/* THPT Exam Practice Card (lines 244-257) */}
              <div className='bg-surface-light dark:bg-surface-dark rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col h-fit sticky top-24'>
                <div className='mb-4'>
                  <div className='h-10 w-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center mb-4 text-blue-600 dark:text-blue-400'>
                    <span className='material-icons-round'>school</span>
                  </div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                    Luyện thi THPT
                  </h3>
                  <p className='text-sm text-gray-600 dark:text-gray-400 leading-relaxed'>
                    Luyện tập với kho đề thi thử chuẩn cấu trúc Bộ GD&ĐT. (Tùy chọn).
                  </p>
                </div>
                <Link
                  to='/thi-thu-thpt'
                  className='mt-auto w-full bg-secondary hover:bg-blue-800 text-white font-medium py-2 px-4 rounded-lg transition-colors shadow-sm text-sm text-center'
                >
                  Luyện đề ngay
                </Link>
              </div>

              {/* Support Card (lines 258-269) */}
              <div className='bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-800 dark:to-black rounded-xl shadow-sm p-6 text-white relative overflow-hidden'>
                <div className='absolute -bottom-4 -right-4 text-white opacity-5'>
                  <span className='material-icons-round text-9xl'>help_outline</span>
                </div>
                <h3 className='text-base font-bold mb-2 relative z-10'>Cần hỗ trợ?</h3>
                <p className='text-xs text-gray-300 mb-4 relative z-10'>
                  Nếu bạn gặp khó khăn trong quá trình làm bài, hãy liên hệ với chúng tôi.
                </p>
                <Link
                  to='/support'
                  className='text-xs font-semibold text-primary hover:text-white transition-colors flex items-center gap-1 relative z-10'
                >
                  Liên hệ ngay <span className='material-icons-round text-sm'>arrow_right_alt</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <UnifiedFooter />
    </div>
  );
}
